import React, { useState } from "react";
import {
  getFirestore,
  collection,
  addDoc,
} from "firebase/firestore";
import { app } from "../firebase";
import "./PreviewPage.css";
import { getAuth } from "firebase/auth";
import BottomNav from "./BottomNav";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import { notificationService } from '../utils/notificationService';
import { getImageUrl } from '../utils/imageUrls';
import { resolveAuthorName } from '../utils/authorLookup';

const db = getFirestore(app);

const PreviewPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    localPreview,                 // local image from the upload page (no re-download)
    imageUrl: passedImageUrl,     // older flows may still pass a URL
    filePath,
    title: passedTitle,
    tags: passedTags,
    description: passedDesc,
  } = location.state || {};

  const title = passedTitle || "";
  const tags = passedTags || [];
  const description = passedDesc || "";

  const [posting, setPosting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  // Show the local image first. If it is gone (page refresh), fall back to the
  // public URL built from filePath.
  const firstChoice = localPreview || passedImageUrl || null;
  const [useFirst, setUseFirst] = useState(!!firstChoice);
  const publicSrc = filePath ? getImageUrl(filePath) : null;
  const previewSrc = useFirst && firstChoice ? firstChoice : publicSrc;

  const handlePost = async () => {
    try {
      const auth = getAuth(app);
      const user = auth.currentUser;

      if (!user) {
        notificationService.showWarning("You must be signed in to post.");
        return;
      }
      if (!filePath) {
        notificationService.showError("Missing image path. Please try uploading again.");
        return;
      }
      if (posting) return;

      setPosting(true);

      // Never store the email address on a public idea
      const authorName = await resolveAuthorName(user);

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timed out. Network slow or blocked.")), 45000)
      );

      await Promise.race([
        addDoc(collection(db, "postedIdeas"), {
          title: title.trim(),
          tags: tags,
          ownerId: user.uid,
          authorName,
          description: description.trim(),
          imagePath: filePath,
          createdAt: new Date(),
          uid: user.uid,
        }),
        timeoutPromise
      ]);

      navigate("/post-success");
    } catch (err) {
      console.error("Post error:", err.message);
      notificationService.showError("Failed to post: " + err.message);
      setPosting(false);
    }
  };

  return (
    <div className="postpreview-page">
      <header className="postpreview-header">
        <button
          type="button"
          className="postpreview-back-btn"
          aria-label="Go back"
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft />
        </button>
        <h1 className="postpreview-page-title">Preview</h1>
      </header>

      <main className="postpreview-sheet">
        <p className="postpreview-idea-title">{title}</p>

        <div className="postpreview-photo">
          {previewSrc ? (
            <img
              src={previewSrc}
              alt="Idea preview"
              className="postpreview-image"
              onClick={() => setShowPopup(true)}
              onError={() => setUseFirst(false)}
            />
          ) : (
            <div className="postpreview-placeholder">Loading image…</div>
          )}
        </div>

        <div className="postpreview-tags">
          {tags.map((tag) => (
            <span key={tag} className="postpreview-tag">{tag}</span>
          ))}
        </div>

        <p className="postpreview-description">{description}</p>

        <button
          className="postpreview-post-btn"
          onClick={handlePost}
          disabled={posting}
        >
          Post Now
        </button>

        {posting && (
          <div className="postpreview-publish-overlay">
            <div className="postpreview-spinner"></div>
            <h3>Publishing idea…</h3>
            <p>Please wait a moment.</p>
          </div>
        )}
      </main>

      {showPopup && (
        <div className="postpreview-zoom-overlay">
          <button
            type="button"
            className="postpreview-zoom-close"
            aria-label="Close image"
            onClick={() => setShowPopup(false)}
          >
            &times;
          </button>
          <div className="postpreview-zoom-frame">
            <Zoom>
              <img
                src={previewSrc}
                alt="Zoomed idea preview"
                className="postpreview-zoom-img"
              />
            </Zoom>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default PreviewPage;